import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Division, EmptyState, Heading, Icons, Image } from "@ir/ui";
import { useUser } from "@/hooks";
import LoadingSpinner from "@/presentation/ui/components/common/LoadingSpinner";
import { GetUserDetailsUseCase } from "@/core/usecases/auth";
import { AuthRepository } from "@/infrastructure/repositories/auth";
import { assetsImages } from "@/assets";

/**
 * Component to handle brand_key switching for already authenticated users.
 * Checks for brand_key in URL query params and switches to the requested brand if valid.
 */
export const BrandKeyHandler: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userBrands, isLoading: isUserLoading } = useUser();
  const [isSwitching, setIsSwitching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const processingRef = useRef<string | null>(null);
  const authRepositoryRef = useRef(new AuthRepository());

  // Extract brand_key from URL query params
  const brandKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("brand_key");
  }, [location.search]);

  // Check if user has access to the requested brand
  const hasAccess = useMemo(() => {
    if (!brandKey || !userBrands) return false;
    return userBrands.some(brand => brand.brandKey === brandKey);
  }, [brandKey, userBrands]);

  // Helper function to remove brand_key from URL and navigate
  const removeBrandKeyFromUrl = useCallback(
    (shouldReload = false) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("brand_key");
      const newSearch = searchParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

      if (shouldReload) {
        globalThis.location.href = newUrl;
      } else {
        navigate(newUrl, { replace: true });
      }
    },
    [location.search, location.pathname, navigate]
  );

  // Helper function to handle error state
  const handleError = useCallback(
    (message: string) => {
      processingRef.current = null;
      setTimeout(() => {
        setErrorMessage(message);
      }, 0);
      removeBrandKeyFromUrl();
    },
    [removeBrandKeyFromUrl]
  );

  // Helper function to handle successful brand switch
  const handleSuccess = useCallback(() => {
    removeBrandKeyFromUrl(true);
  }, [removeBrandKeyFromUrl]);

  // Helper function to handle brand switch failure
  const handleSwitchFailure = useCallback(
    (message: string) => {
      processingRef.current = null;
      setErrorMessage(message);
      setIsSwitching(false);
      removeBrandKeyFromUrl();
    },
    [removeBrandKeyFromUrl]
  );

  // Main function to switch brands
  const switchBrand = useCallback(
    (targetBrandKey: string) => {
      // Mark that we're processing this brand_key
      processingRef.current = targetBrandKey;

      // Set loading state
      setTimeout(() => {
        setIsSwitching(true);
      }, 0);

      // Execute brand switch
      new GetUserDetailsUseCase(authRepositoryRef.current)
        .execute(targetBrandKey)
        .then(result => {
          if (result.type === "success") {
            handleSuccess();
          } else {
            handleSwitchFailure(
              "There was a problem switching to the requested brand. Please try again, or contact support if the issue persists."
            );
          }
        })
        .catch(() => {
          handleSwitchFailure("There was a problem switching to the requested brand. Please try again.");
        });
    },
    [handleSuccess, handleSwitchFailure]
  );

  // Main effect to handle brand_key processing
  useEffect(() => {
    // Only process brand_key if user and brands are loaded
    if (isUserLoading || !userBrands) {
      return;
    }

    // If no brand_key in URL, nothing to do
    if (!brandKey) {
      processingRef.current = null;
      return;
    }

    // If we're already processing this brand_key, don't process again
    if (processingRef.current === brandKey) {
      return;
    }

    // Check current brand from storage to see if we're already on the correct brand
    authRepositoryRef.current
      .getUserCurrentBrandKeyFromStorage()
      .then(currentBrandKey => {
        // If already on the correct brand, just remove the query param
        if (currentBrandKey === brandKey) {
          processingRef.current = null;
          removeBrandKeyFromUrl();
          return;
        }

        // If user doesn't have access, show error
        if (!hasAccess) {
          handleError("The brand is invalid or you do not have permission to access this page.");
          
          return;
        }

        // Switch to the requested brand
        switchBrand(brandKey);
      })
      .catch(() => {
        // If we can't get current brand, proceed with brand switch if user has access
        if (!hasAccess) {
          handleError("The brand is invalid or you do not have permission to access this page.");
          return;
        }

        // Switch to the requested brand
        switchBrand(brandKey);
      });
  }, [brandKey, hasAccess, isUserLoading, userBrands, removeBrandKeyFromUrl, handleError, switchBrand]);

  // Handler to dismiss error message
  const handleDismissError = useCallback(() => {
    sessionStorage.clear();
    localStorage.clear();
    navigate("/", { replace: true, flushSync: true, viewTransition: false });
    globalThis.location.reload();
  }, [navigate]);

  // Loading state
  if (isSwitching) {
    return <LoadingSpinner />;
  }

  // Error state
  if (errorMessage) {
    return (
      <Division className="d-flex flex-1 flex-align-center flex-justify-center">
        <Division>
          <Division className="d-flex flex-align-center flex-justify-center pb-4">
            <Image style={{ width: "220px" }} src={assetsImages.IRLogoFull} alt="IR Logo" />
          </Division>
          <EmptyState
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                Brand Access Error
              </Heading>
            }
            description={errorMessage}
            icon={Icons.InfoOctagon}
            className="p-4"
          >
            <Button onPress={handleDismissError}>Dismiss</Button>
          </EmptyState>
        </Division>
      </Division>
    );
  }

  return <>{children}</>;
};
